import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';

import Badge from '../components/common/Badge';
import { Button, Card, PAY_PER_MEETING, transcriptionState } from '@copilot/ui';
import Loader from '../components/common/Loader';
import { Tabs } from '@copilot/ui';
import CoPilotPanel from '../components/meeting/CoPilotPanel';
import EndMeetingModal from '../components/meeting/EndMeetingModal';
import StatusBar from '../components/meeting/StatusBar';
import TranscriptPanel from '../components/meeting/TranscriptPanel';
import { microphoneEvents } from '../audioClient';
import {
  AUDIO_SOURCES,
  canCaptureMeetingTab,
  isPhoneOrTablet,
  isTabBasedAudioSource,
  resolveCaptureSource,
} from '../constants/audioSources';
import {
  CO_PILOT_MODES,
  LANGUAGES,
  SOURCE_LANGUAGE_AUTO,
} from '../constants/languages';
import { useAudio } from '../hooks/useAudio';
import { useAuth } from '../hooks/useAuth';
import { logDebug, reportError } from '../services/errorTracking';
import { WS_BASE_URL } from '../services/httpClient';
import {
  STANDARD_MEETING_CODE,
  STANDARD_MEETING_ENDED_MESSAGE,
  STANDARD_MEETING_ENDED_TITLE,
  WARNING_MESSAGES,
  formatCountdown,
  isTimeLimitCode,
  secondsRemaining,
  warningLevel,
} from '../utils/meetingTimeLimit';
import * as meetingApi from '../services/meetingApi';

function languageLabel(code) {
  if (code === SOURCE_LANGUAGE_AUTO) return 'Auto / Multilingual';
  const match = LANGUAGES.find((lang) => lang.code === code);
  return match ? match.label : code;
}

function modeLabel(value) {
  const match = CO_PILOT_MODES.find((m) => m.value === value);
  return match ? match.label : value;
}

function audioSourceHeaderLabel(source) {
  if (source === AUDIO_SOURCES.MEETING_TAB) return 'Meeting Tab Audio';
  if (source === AUDIO_SOURCES.MIXED) return 'Microphone + Meeting';
  return 'Microphone';
}

const MAX_RECONNECT_ATTEMPTS = 5;

// NotAllowedError on a phone: the microphone prompt was declined, or the
// browser itself has no microphone permission. In-app browsers (the Google
// app, Instagram, ...) often never ask at all.
const PHONE_MIC_BLOCKED_MESSAGE =
  'Microphone access is blocked. Tap Start Listening and choose Allow. ' +
  'If no prompt appears, allow the microphone for this browser in your ' +
  "phone's Settings, or open this page in Safari or Chrome instead of " +
  "another app's built-in browser.";

// models/meeting.py MeetingStatus. Only these strings are used here:
// ENDED is terminal (no live socket, ever), everything else is allowed
// to open /ws/audio (the backend still does the SCHEDULED/PAUSED ->
// ACTIVE transition, or rejects, as the authoritative gate).
const MEETING_STATUS_ENDED = 'ended';

// A long meeting (hours, thousands of utterances) must not grow the
// live in-process transcript state without bound - the full, durable
// history is already persisted server-side and is what
// MeetingDetailsPage/MeetingTranscriptPanel show after the meeting ends;
// this cap only bounds what's held/rendered DURING the live call, so
// nothing is ever actually lost. Oldest entries are dropped first.
const MAX_LIVE_TRANSCRIPT_ENTRIES = 500;

// Matches the close codes routers/websocket.py uses for auth/lifecycle
// rejections — these must never trigger a reconnect loop, since retrying
// would just fail identically every time.
const WS_CLOSE_UNAUTHENTICATED = 4401;
const WS_CLOSE_FORBIDDEN = 4403;
const WS_CLOSE_MEETING_ENDED = 4409;
// The subscription refused this meeting (backend/routers/websocket.py's
// CLOSE_USAGE_LIMIT) - e.g. no active plan. Plans do not limit meeting
// minutes or length. Like the other codes here it must never be retried -
// reconnecting would be refused identically.
const WS_CLOSE_USAGE_LIMIT = 4402;
// Another one of the user's meetings is already ACTIVE/PAUSED
// (backend/routers/websocket.py's CLOSE_MEETING_CONFLICT) - never
// retryable, reconnecting would be refused identically until that
// other meeting is ended.
const WS_CLOSE_MEETING_CONFLICT = 4404;
// This meeting was paused from elsewhere (another tab, or an admin
// action) while this socket stayed open (backend/routers/websocket.py's
// CLOSE_MEETING_PAUSED). Not an error and not retried automatically -
// mirrors the same UI state a self-initiated Pause already sets, so
// Resume keeps working normally from here.
const WS_CLOSE_MEETING_PAUSED = 4405;
// The connected user's own AUTHENTICATION was revoked - logout-all, a
// password change/reset, or an admin disabling/deleting the account
// (backend/routers/websocket.py's CLOSE_AUTH_REVOKED). Deliberately NOT
// treated like WS_CLOSE_UNAUTHENTICATED's "maybe transient, try
// refreshing" handling below: the session was deliberately revoked, so
// a refresh would fail identically. Never retried.
const WS_CLOSE_AUTH_REVOKED = 4406;

// The backend's code for a meeting bought with a Pay Per Meeting pass
// reaching its time limit (services/subscription_service.py).
const PASS_DURATION_REACHED = 'MEETING_PASS_DURATION_REACHED';

const USAGE_LIMIT_FALLBACK_MESSAGE =
  'Your plan does not allow this meeting right now. Check your plan to continue.';

function formatElapsed(seconds) {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;

  const pad = (n) => String(n).padStart(2, '0');

  return `${pad(h)}:${pad(m)}:${pad(s)}`;
}

function LiveMeetingPage() {
  const { meetingId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  // Destructured to the individual, useCallback-stable functions on
  // purpose: a lifecycle-sensitive effect (reconnect teardown, terminal
  // transition) must never depend on an aggregate `audio` object whose
  // identity could churn on an unrelated rerender.
  const { start: startAudio, stop: stopAudio, sendControl } = useAudio();
  const { user, refreshUser } = useAuth();

  const microphoneId = location.state?.microphoneId;

  const [meeting, setMeeting] = useState(null);
  const [loadError, setLoadError] = useState('');
  // Shown when the microphone cannot be captured alongside the shared
  // meeting tab - the tab audio keeps running, but the user's own speech
  // will not be in the transcript, and they should know.
  const [micNotice, setMicNotice] = useState('');
  // The microphone being captured (its name is shown in the header, so which
  // device is being heard is never a guess) and whether it is muted right now.
  const [micInfo, setMicInfo] = useState(null);
  const [micMuted, setMicMuted] = useState(false);
  // The microphone is open but no sound is coming from it, and the name of a
  // different default microphone the system switched to while this one is in
  // use (empty when it did not).
  const [micSilent, setMicSilent] = useState(false);
  const [micDefaultChanged, setMicDefaultChanged] = useState('');
  const [status, setStatus] = useState('starting');

  // State keyed by the STABLE server-assigned transcript_id, not by
  // "last message received" — translation/AI events can arrive out of
  // order (e.g. AI for transcript #3 finishing before translation for
  // #1) and must still attach to the correct card. transcriptOrder
  // tracks arrival order for rendering; transcriptsById is the source
  // of truth each event updates by id.
  const [transcriptOrder, setTranscriptOrder] = useState([]);
  const [transcriptsById, setTranscriptsById] = useState({});
  // Memoized so the 1-second elapsed-timer tick (and other unrelated
  // state updates) don't hand TranscriptPanel a brand-new array on every
  // render — combined with TranscriptPanel/TranscriptEntry being
  // React.memo'd, this avoids re-rendering the entire (potentially long)
  // transcript list every second.
  const entries = useMemo(
    () => transcriptOrder.map((id) => transcriptsById[id]).filter(Boolean),
    [transcriptOrder, transcriptsById]
  );
  // What the left panel shows (backend transcription_enabled / _scope, fixed
  // when the meeting was created): the whole transcript beside its
  // translation; or, for an Interview and for a Meeting with Show Live
  // Transcript off, only the questions KrishvaQ detected - speech recognition
  // still runs, it just feeds the live answers.
  const panelVariant = useMemo(() => {
    const state = meeting ? transcriptionState(meeting) : 'full';
    return state === 'full' ? 'full' : 'questions';
  }, [meeting]);
  const panelEntries = useMemo(
    () =>
      panelVariant === 'questions'
        ? entries.filter(
            (entry) =>
              entry.isQuestion || entry.answer || entry.isGeneratingAnswer || entry.answerError
          )
        : entries,
    [panelVariant, entries]
  );
  const [interimText, setInterimText] = useState('');
  // Ephemeral "Translation · Live" preview of the CURRENT interim
  // utterance — never attached to a transcript_id (the utterance isn't
  // finalized/persisted yet). Cleared the moment the utterance
  // finalizes (final_transcript) so it can never linger and get
  // mistaken for that entry's authoritative translation. revision only
  // guards against an out-of-order arrival slipping through despite the
  // backend already suppressing stale live_translation sends itself.
  const [interimTranslation, setInterimTranslation] = useState('');
  const interimTranslationRevisionRef = useRef(0);
  const [currentQuestionId, setCurrentQuestionId] = useState(null);

  // True once the backend has told us this meeting is already over
  // (ENDED) — either from this tab's own End Meeting action or because
  // the WebSocket was rejected with WS_CLOSE_MEETING_ENDED (e.g. ended
  // from another tab/device). Drives StatusBar to show a terminal
  // "Meeting Ended" state instead of actionable Pause/End buttons that
  // would just fail against the backend's lifecycle validation.
  const [meetingEnded, setMeetingEnded] = useState(false);

  // Set when the backend stops this session for a plan/usage reason.
  // Kept separate from loadError so the UI can offer an upgrade link
  // rather than a generic retry for something retrying cannot fix.
  const [usageLimitMessage, setUsageLimitMessage] = useState('');
  const [usageLimitCode, setUsageLimitCode] = useState('');

  // Surfaced in StatusBar as "Reconnecting... Attempt N/MAX" while
  // status === 'reconnecting' — reconnectAttemptsRef already tracked
  // this internally (for backoff timing) but it was never exposed to
  // the UI, which just showed a generic "Reconnecting..." with no sense
  // of progress or whether it's about to give up.
  const [reconnectAttempt, setReconnectAttempt] = useState(0);

  const [showEndModal, setShowEndModal] = useState(false);
  const [isEndingMeeting, setIsEndingMeeting] = useState(false);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  // The server's clock for this meeting (the REST `time_limit`, refreshed by
  // the socket's `session_time_limit` event), and the current time it is
  // counted against. Display only: the backend ends the meeting itself.
  const [timeLimit, setTimeLimit] = useState(null);
  const [clockNowMs, setClockNowMs] = useState(() => Date.now());
  const [mobileTab, setMobileTab] = useState('transcript');

  const reconnectAttemptsRef = useRef(0);
  const reconnectTimeoutRef = useRef(null);
  const isEndingRef = useRef(false);
  // Latched once the meeting is known to be ENDED - on load (GET says
  // status "ended"), from this tab's End Meeting, or from a mid-session
  // lifecycle close (4409). A terminal state: it must permanently stop
  // any (re)connect attempt, readable synchronously from the socket
  // callbacks that are installed once and can't see current state.
  const meetingEndedRef = useRef(false);
  // A WebSocket connection can't refresh its own access cookie the way
  // REST requests can (httpClient transparently refreshes on a 401 and
  // retries) — the 15-minute access cookie can simply expire while a
  // long-lived socket keeps working, and only surfaces as 4401 on the
  // NEXT connection attempt (reconnect after a network blip), even
  // though the refresh token is still perfectly valid. This ref makes
  // sure we only try the refresh-then-retry dance once per disconnect,
  // never a loop, in case the refresh token itself has also expired.
  const authRefreshAttemptedRef = useRef(false);
  // Wall-clock source of truth for the displayed timer — set ONCE (from
  // the backend's meeting.started_at, which is what
  // duration_seconds = ended_at - started_at is actually computed from
  // server-side) and never reset by a reconnect. Ticking off this
  // instead of "increment a counter while status === 'listening'" is
  // what keeps the frontend timer and the backend's stored duration in
  // agreement: the backend's duration includes paused time (Pause just
  // stops accepting new audio, it doesn't stop the wall clock), so the
  // frontend must keep counting through a pause too, not freeze it.
  const startedAtMsRef = useRef(null);
  // Distinguishes first-ever tab connect ("Select Meeting Tab") from a
  // later re-share after a drop/Pause/Stop-sharing ("Reconnect Meeting
  // Audio") for copy only — the underlying flow (explicit click ->
  // getDisplayMedia()) is identical either way.
  const hasConnectedTabAudioRef = useRef(false);
  // Guards the one-time reload hydration of live transcript/answer state
  // from the persisted history (see loadMeeting) so a reconnect or a
  // second effect run can't seed it twice.
  const didHydrateRef = useRef(false);
  // Blocks a second startListening() call (fast double-click on "Select
  // Meeting Tab" before React re-renders the button away) from firing
  // while getDisplayMedia()/the WS handshake is still pending. `status`
  // itself can't be used for this check since the setStatus('starting')
  // below hasn't committed yet when a synchronous second click lands.
  const isStartingRef = useRef(false);

  const clearPendingReconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
  }, []);

  // THE single terminal-state transition. Every path that discovers this
  // meeting is already ENDED - the initial GET returning status
  // "ended", a mid-session 4409 close, or a Pause/Resume/End PATCH
  // rejected with MEETING_NOT_MODIFIABLE - funnels through here instead
  // of each partially updating its own subset of state (which is exactly
  // how the UI ended up with meetingEndedRef=true, meetingEnded=false
  // and status='disconnected' at the same time). It latches the ref
  // synchronously (socket callbacks read it), permanently stops any
  // (re)connect, stops capture, drops stale transient disconnect/
  // reconnect errors, and closes the End modal / ending state.
  const enterEndedState = useCallback(() => {
    meetingEndedRef.current = true;
    isEndingRef.current = false;
    clearPendingReconnect();
    reconnectAttemptsRef.current = 0;
    authRefreshAttemptedRef.current = false;
    stopAudio();
    setReconnectAttempt(0);
    setMeetingEnded(true);
    setStatus('ended');
    setShowEndModal(false);
    setIsEndingMeeting(false);
    // A meeting that is simply over is not a connection failure - clear
    // any "Disconnected"/"Reconnecting..." banner left over from the
    // socket closing.
    setLoadError('');
    setUsageLimitMessage('');
  }, [clearPendingReconnect, stopAudio]);

  const patchTranscript = useCallback((transcriptId, patch) => {
    setTranscriptsById((prev) => ({
      ...prev,
      [transcriptId]: { ...prev[transcriptId], ...patch },
    }));
  }, []);

  // The latest transcripts, readable from handleSelectQuestion (a stable
  // callback) so it can reject an id the live view has never seen
  // without depending on transcriptsById and being recreated on every
  // socket message.
  const transcriptsByIdRef = useRef(transcriptsById);
  transcriptsByIdRef.current = transcriptsById;

  // Point the KrishvaQ panel at an already-known question. No socket
  // message, no new answer — it only changes which in-memory entry is
  // shown. Ignored for an id the live view has never seen. The live
  // events below (question / answer_start / answer_error) still move the
  // selection to the newest turn on their own, so a new question always
  // takes over.
  const handleSelectQuestion = useCallback((transcriptId) => {
    if (transcriptsByIdRef.current[transcriptId]) {
      setCurrentQuestionId(transcriptId);
    }
  }, []);

  // Re-runs a failed answer/translation over the live audio socket
  // (backend: MeetingSession.handle_control_message). Clears the error
  // and shows progress optimistically so the click has visible effect;
  // if the provider is still down, the next answer_error/
  // translation_error simply replaces it with the current reason.
  const handleRetryAnswer = useCallback(
    (transcriptId) => {
      const sent = sendControl({
        type: 'retry_answer',
        transcript_id: transcriptId,
      });

      if (!sent) {
        return;
      }

      patchTranscript(transcriptId, {
        answerError: null,
        isGeneratingAnswer: true,
      });
    },
    [sendControl, patchTranscript]
  );

  const handleRetryTranslation = useCallback(
    (transcriptId) => {
      const sent = sendControl({
        type: 'retry_translation',
        transcript_id: transcriptId,
      });

      if (!sent) {
        return;
      }

      patchTranscript(transcriptId, { translationError: null });
    },
    [sendControl, patchTranscript]
  );

  // The AI answer streamed fine but was wrong; the user typed what was
  // actually meant. Send it over the live audio socket (backend:
  // MeetingSession.handle_control_message's correct_answer branch) and
  // optimistically flip the panel back to "Thinking..." — the forced
  // re-answer arrives as the usual answer_start / answer_delta /
  // answer_complete sequence and overwrites the answer in place.
  const handleCorrectAnswer = useCallback(
    (transcriptId, guidance) => {
      if (!guidance) {
        return;
      }

      const sent = sendControl({
        type: 'correct_answer',
        transcript_id: transcriptId,
        guidance,
      });

      if (!sent) {
        return;
      }

      patchTranscript(transcriptId, {
        answer: '',
        answerError: null,
        isGeneratingAnswer: true,
        isStreamingAnswer: false,
      });
    },
    [sendControl, patchTranscript]
  );

  // "Regenerate as Code" / "Explain as Theory": re-answers the same turn in
  // that shape (backend: the ask_ai branch with an answer_format). The new
  // answer replaces this one in place as a new revision - the same overwrite
  // path a correction takes - but it is not feedback and changes no meeting
  // or account setting. No optimistic state: the backend answers an accepted
  // request with answer_queued (which shows "Thinking..."), and ignores one
  // for a turn whose answer is still being written - a local "Thinking..."
  // would then never resolve.
  const handleRegenerateAnswer = useCallback(
    (transcriptId, answerFormat) => {
      sendControl({
        type: 'ask_ai',
        transcript_id: transcriptId,
        answer_format: answerFormat,
      });
    },
    [sendControl]
  );

  const handleMessage = useCallback(
    (data) => {
      switch (data.type) {
        case 'interim_transcript':
          setInterimText(data.text);
          break;

        case 'final_transcript': {
          setInterimText('');
          // The authoritative final translation (once it arrives) fully
          // replaces this preview — clear it now so it can never be
          // mistaken for this now-finalized entry's translation.
          setInterimTranslation('');

          const id = data.transcript_id;

          setTranscriptsById((prev) => ({
            ...prev,
            [id]: {
              id,
              original: data.text,
              // 'microphone' | 'system_audio' | 'legacy_unknown' - which
              // capture produced the row (backend persists the same value).
              audioSource: data.audio_source || null,
              translated: null,
              isQuestion: !!data.is_question,
              timestamp: data.created_at ? new Date(data.created_at) : new Date(),
              answer: null,
              answerError: null,
              isGeneratingAnswer: !!data.is_question,
            },
          }));

          // Guards against a duplicate final_transcript for the same
          // transcript_id (e.g. a redelivered WebSocket message)
          // rendering the same entry twice, and caps the sliding window
          // so a long meeting's live state can't grow unbounded — see
          // MAX_LIVE_TRANSCRIPT_ENTRIES above. droppedIds is populated
          // synchronously by the updater below (both calls happen in the
          // same handleMessage invocation, so this plain variable is
          // safe here) and used right after to also free the dropped
          // entries from transcriptsById, not just hide them from order.
          let droppedIds = [];
          setTranscriptOrder((prev) => {
            if (prev.includes(id)) return prev;

            const next = [...prev, id];
            if (next.length <= MAX_LIVE_TRANSCRIPT_ENTRIES) return next;

            const overflow = next.length - MAX_LIVE_TRANSCRIPT_ENTRIES;
            droppedIds = next.slice(0, overflow);
            return next.slice(overflow);
          });

          if (droppedIds.length > 0) {
            setTranscriptsById((prev) => {
              const next = { ...prev };
              droppedIds.forEach((droppedId) => delete next[droppedId]);
              return next;
            });
          }

          if (data.is_question) {
            setCurrentQuestionId(id);
          }
          break;
        }

        case 'live_translation':
          if (data.revision >= interimTranslationRevisionRef.current) {
            interimTranslationRevisionRef.current = data.revision;
            setInterimTranslation(data.text);
          }
          break;

        case 'translation':
          patchTranscript(data.transcript_id, {
            translated: data.text,
            translationError: null,
          });
          break;

        case 'translation_error':
          patchTranscript(data.transcript_id, {
            translationError: data.message,
          });
          break;

        case 'question':
          // Sent by the backend's semantic intent worker just before
          // `answer_queued`, only for turns classified as answer-worthy.
          // Plain statements never receive it (or any answer_* event).
          patchTranscript(data.transcript_id, { isQuestion: true });
          setCurrentQuestionId(data.transcript_id);
          break;

        // Legacy one-shot event — kept for backward compatibility with
        // any path that might still emit it (see routers/websocket.py).
        case 'answer':
          patchTranscript(data.transcript_id, {
            answer: data.answer,
            aiResponseId: data.ai_response_id,
            isGeneratingAnswer: false,
            isStreamingAnswer: false,
            answerError: null,
          });
          break;

        case 'answer_queued':
          // The backend accepted this finalized turn as a meaningful
          // conversational turn and an AI reply IS coming — sent
          // immediately, before context building and before the first
          // provider token. This is what moves the KrishvaQ panel off
          // "Waiting for the conversation" and into a "Thinking..."
          // state the instant the participant finishes speaking, instead
          // of it lagging until answer_start (first OpenAI token).
          setCurrentQuestionId(data.transcript_id);
          patchTranscript(data.transcript_id, {
            answer: '',
            isGeneratingAnswer: true,
            isStreamingAnswer: false,
            answerError: null,
          });
          break;

        case 'answer_start':
          // Actual provider streaming has begun. If answer_queued was
          // missed (older backend, or a reconnect mid-turn) this still
          // drives the panel on its own.
          setCurrentQuestionId(data.transcript_id);
          patchTranscript(data.transcript_id, {
            answer: '',
            isGeneratingAnswer: true,
            isStreamingAnswer: true,
            answerError: null,
            // Whether the question is code-related (absent from an older
            // backend) - decides Regenerate as Code / Explain as Theory.
            codeRelated: data.code_related ?? null,
          });
          break;

        case 'answer_delta':
          setTranscriptsById((prev) => {
            const existing = prev[data.transcript_id];
            if (!existing) return prev;
            return {
              ...prev,
              [data.transcript_id]: {
                ...existing,
                answer: (existing.answer || '') + data.delta,
              },
            };
          });
          break;

        case 'answer_complete':
          patchTranscript(data.transcript_id, {
            answer: data.answer,
            aiResponseId: data.ai_response_id,
            answerRevision: data.revision,
            ...(typeof data.code_related === 'boolean'
              ? { codeRelated: data.code_related }
              : {}),
            isGeneratingAnswer: false,
            isStreamingAnswer: false,
            answerError: null,
          });
          break;

        case 'answer_error':
          // A hard failure can arrive with no preceding answer_start
          // (e.g. the provider circuit is open) — still surface it in
          // the conversational panel.
          setCurrentQuestionId(data.transcript_id);
          patchTranscript(data.transcript_id, {
            isGeneratingAnswer: false,
            isStreamingAnswer: false,
            answerError: data.message,
          });
          break;

        // The backend sends this immediately BEFORE closing the socket
        // with WS_CLOSE_USAGE_LIMIT, so the real reason is known even
        // though the close event itself carries no message.
        case 'usage_limit_reached':
          setUsageLimitMessage(data.message || USAGE_LIMIT_FALLBACK_MESSAGE);
          setUsageLimitCode(data.code || '');
          // A fixed length used up: the backend has already ENDED the
          // meeting, so the countdown stops and nothing reconnects.
          if (isTimeLimitCode(data.code)) meetingEndedRef.current = true;
          break;

        // The server's own deadline for this meeting, sent as the session
        // starts (and again on every reconnect, with the SAME deadline).
        case 'session_time_limit': {
          const { type: _type, ...limit } = data;
          setTimeLimit(limit);
          break;
        }

        default:
          break;
      }
    },
    [patchTranscript]
  );

  // Handles an UNEXPECTED drop (network blip, server restart) — never
  // fires for an explicit Pause/End Meeting, since those close the
  // socket with __explicitClose set (see audioClient.js). Auth/lifecycle
  // rejection codes must never be retried: they'd fail identically every
  // time. Everything else gets a bounded, backoff-spaced reconnect.
  const handleDisconnected = useCallback(
    (event) => {
      if (isEndingRef.current) return;

      // A meeting that has ended is terminal - never reconnect, whatever
      // the close code. Reconnecting would just be refused identically.
      if (meetingEndedRef.current) {
        enterEndedState();
        return;
      }

      const code = event?.code;

      if (code === WS_CLOSE_MEETING_ENDED) {
        // The meeting ended under an open socket (this tab's End Meeting
        // races the close, or it was ended elsewhere). Latch terminal
        // and stop - no reconnect.
        logDebug('meeting_lifecycle', 'WEBSOCKET_MEETING_ENDED');
        enterEndedState();
        return;
      }

      if (code === WS_CLOSE_USAGE_LIMIT) {
        // Audio capture must stop too: the backend has already torn the
        // session down, so leaving the microphone/tab capture running
        // would only look like the meeting is still being recorded.
        stopAudio();
        setStatus('disconnected');
        setUsageLimitMessage(
          (current) => current || USAGE_LIMIT_FALLBACK_MESSAGE
        );
        return;
      }

      if (code === WS_CLOSE_MEETING_PAUSED) {
        // Paused from elsewhere - not an error, so this mirrors the
        // same state handlePause already sets for a self-initiated
        // pause rather than showing a disconnected/error message.
        // Resume (PATCH status=active + a fresh socket) keeps working
        // normally from here.
        stopAudio();
        setStatus('paused');
        return;
      }

      if (code === WS_CLOSE_MEETING_CONFLICT) {
        // The backend has already refused/torn down this session - stop
        // capture rather than leave it looking like the meeting is
        // still being recorded (same reasoning as WS_CLOSE_USAGE_LIMIT
        // above).
        stopAudio();
        setStatus('disconnected');
        setLoadError(
          'Another meeting is already in progress. End it before starting this meeting.'
        );
        return;
      }

      if (code === WS_CLOSE_AUTH_REVOKED) {
        stopAudio();
        setStatus('disconnected');
        setLoadError('Your session has ended. Please log in again.');

        // Reuses the SAME mechanism a normal unrecoverable 401 already
        // triggers, rather than introducing a separate auth flow here:
        // this call fails (both the access AND refresh token are
        // invalid once the session is revoked), and AuthContext's
        // existing unauthorized handler clears the user and lets
        // ProtectedRoute redirect to /login. No reconnect is attempted.
        refreshUser().catch(() => {});
        return;
      }

      if (code === WS_CLOSE_FORBIDDEN) {
        // WS_CLOSE_MEETING_ENDED is handled above (terminal, no reconnect).
        setStatus('disconnected');
        setLoadError(
          'You are no longer authorized for this meeting. Please log in again.'
        );
        return;
      }

      // What THIS browser can capture, not what the meeting was stored
      // as: a phone cannot capture a tab, so a tab-based meeting opened
      // there listens through the microphone and reconnects like one.
      const tabBased = isTabBasedAudioSource(
        resolveCaptureSource(meeting?.audio_source)
      );
      // A phone can only restart its microphone from a tap too (see
      // startMicStream), so it waits for one exactly like a meeting tab.
      const needsTap = tabBased || isPhoneOrTablet();
      const tapStatus = tabBased ? 'awaiting_tab_selection' : 'awaiting_mic_start';

      if (code === WS_CLOSE_UNAUTHENTICATED) {
        if (authRefreshAttemptedRef.current) {
          setStatus('disconnected');
          setLoadError('Your session has expired. Please log in again.');
          return;
        }

        authRefreshAttemptedRef.current = true;
        setStatus('reconnecting');

        // The access cookie can expire while the socket stays open; a
        // 4401 on reconnect doesn't necessarily mean the user's session
        // is actually over. Refresh through the normal HTTP flow once
        // before giving up, then retry the WebSocket with the new cookie.
        refreshUser()
          .then(() => {
            if (needsTap) {
              // Session refresh happened across an async/network round
              // trip — that is NOT a fresh user gesture, and browsers
              // block getDisplayMedia() outside one. Require an
              // explicit click instead of silently reopening the picker.
              stopAudio();
              setStatus(tapStatus);
            } else {
              // eslint-disable-next-line no-use-before-define
              startListening();
            }
          })
          .catch(() => {
            // Refresh failed (refresh token itself expired/invalid) —
            // httpClient's unauthorizedHandler has already cleared the
            // session, so ProtectedRoute will redirect to /login.
            setStatus('disconnected');
            setLoadError('Your session has expired. Please log in again.');
          });
        return;
      }

      // Unexpected drop (network blip, server restart) for meeting-tab/
      // mixed capture: the existing microphone backoff-reconnect loop
      // below would eventually need to call getDisplayMedia() from a
      // setTimeout callback, which browsers block outright (no user
      // gesture). Rather than a risky rewrite that keeps the tab
      // MediaStream alive across a WebSocket reconnect, this uses the
      // safer fallback: clean up and require an explicit re-share click.
      if (needsTap) {
        stopAudio();
        clearPendingReconnect();
        reconnectAttemptsRef.current = 0;
        setStatus(tapStatus);
        setLoadError('Meeting audio disconnected unexpectedly.');
        return;
      }

      reconnectAttemptsRef.current += 1;

      if (reconnectAttemptsRef.current > MAX_RECONNECT_ATTEMPTS) {
        setStatus('disconnected');
        setReconnectAttempt(0);
        setLoadError('Unable to reconnect. Please refresh and try again.');
        return;
      }

      logDebug('meeting_lifecycle', 'WEBSOCKET_RECONNECT');
      setStatus('reconnecting');
      setReconnectAttempt(reconnectAttemptsRef.current);

      const delayMs = Math.min(
        1000 * 2 ** (reconnectAttemptsRef.current - 1),
        16000
      );

      clearPendingReconnect();
      reconnectTimeoutRef.current = setTimeout(() => {
        // eslint-disable-next-line no-use-before-define
        startListening();
      }, delayMs);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [clearPendingReconnect, refreshUser, meeting, stopAudio, enterEndedState]
  );

  const startListening = useCallback(async () => {
    // An ENDED meeting is terminal: /ws/audio will reject the handshake
    // (403). Never even attempt it - and make sure a queued reconnect
    // can't sneak one through either.
    if (meetingEndedRef.current) {
      logDebug('meeting_lifecycle', 'ENDED_MEETING_NO_CONNECT');
      clearPendingReconnect();
      setStatus('ended');
      return;
    }

    if (isStartingRef.current) {
      return;
    }
    isStartingRef.current = true;
    setStatus('starting');
    setMicNotice('');

    try {
      // A tab-based meeting opened in a browser that cannot capture a tab
      // (every phone and tablet browser) is captured through the
      // microphone instead - see resolveCaptureSource.
      const captureSource = resolveCaptureSource(meeting.audio_source);
      // On such a browser the microphone is the only way the other
      // participants are heard, so it is the room, not just the user's
      // own voice. The backend only auto-answers microphone audio when
      // told this (user_preferences_service.is_audio_source_eligible).
      const micHearsOthers =
        captureSource === AUDIO_SOURCES.MICROPHONE && !canCaptureMeetingTab();
      const wsUrl =
        `${WS_BASE_URL}/ws/audio?meeting_id=${meetingId}` +
        (micHearsOthers ? '&mic_hears_others=1' : '');

      await startAudio(
        captureSource,
        wsUrl,
        handleMessage,
        () => setStatus('disconnected'),
        microphoneId,
        handleDisconnected,
        // Only a meeting-tab session also records the microphone; the
        // argument is omitted entirely for a microphone session.
        ...(isTabBasedAudioSource(captureSource)
          ? [
              {
                onMicrophoneUnavailable: (reason) =>
                  setMicNotice(
                    reason === 'unsupported'
                      ? 'Your microphone could not be added to this session, so only the meeting tab is being transcribed.'
                      : reason === 'not_found'
                        ? 'The microphone you chose could not be found - it may be unplugged, or a Bluetooth headset may have switched modes. KrishvaQ will not record a different microphone instead, so only the meeting tab is being transcribed and your own speech will not appear. Reconnect it and pause and resume the meeting, or start a new meeting and choose the microphone again.'
                        : 'Your microphone is not available, so only the meeting tab is being transcribed. Your own speech will not appear in the transcript.'
                  ),
              },
            ]
          : // A browser that cannot capture a tab hears the other
            // participants only through the microphone, so it must not
            // filter the meeting out of it (see startMicStream).
            micHearsOthers
            ? [{ hearOthers: true }]
            : [])
      );

      // The meeting may have gone terminal while the socket was opening
      // - e.g. Resume raced a PATCH that came back MEETING_NOT_MODIFIABLE,
      // or a 4409 landed during the handshake. Don't flip the UI back to
      // "listening" over a terminal state; tear the just-opened socket
      // down instead.
      if (meetingEndedRef.current) {
        enterEndedState();
        return;
      }

      setStatus('listening');
      reconnectAttemptsRef.current = 0;
      setReconnectAttempt(0);
      authRefreshAttemptedRef.current = false;
      hasConnectedTabAudioRef.current = true;

      if (!startedAtMsRef.current) {
        // First-ever connect for this meeting: the initial GET (before
        // the socket existed) ran while the meeting was still
        // SCHEDULED, so it had no started_at yet — the backend only
        // sets it once the WebSocket session actually starts (see
        // MeetingSession.mark_active_if_needed). Refetch once to pick
        // up the authoritative timestamp rather than approximating
        // with the local clock.
        try {
          const fresh = await meetingApi.getMeeting(meetingId);
          startedAtMsRef.current = fresh.started_at
            ? new Date(fresh.started_at).getTime()
            : Date.now();
          // The deadline is fixed once the meeting starts: pick it up now.
          if (fresh.time_limit) setTimeLimit(fresh.time_limit);
        } catch (_) {
          startedAtMsRef.current = Date.now();
        }
      }
    } catch (err) {
      reportError('live_meeting_audio_start', err);
      const phone = isPhoneOrTablet();
      setLoadError(
        phone && err?.name === 'NotAllowedError'
          ? PHONE_MIC_BLOCKED_MESSAGE
          : err?.message || 'Unable to start audio capture.'
      );

      // For meeting-tab/mixed, a failure here is usually the user
      // cancelling the browser's share picker or sharing without audio
      // — let them just try the button again rather than dropping into
      // a scarier generic "disconnected" state. A phone's microphone
      // gets the same retry button.
      setStatus(
        isTabBasedAudioSource(resolveCaptureSource(meeting?.audio_source))
          ? 'awaiting_tab_selection'
          : phone
            ? 'awaiting_mic_start'
            : 'disconnected'
      );
    } finally {
      isStartingRef.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    meetingId,
    microphoneId,
    handleMessage,
    handleDisconnected,
    meeting,
    startAudio,
    enterEndedState,
  ]);

  useEffect(() => clearPendingReconnect, [clearPendingReconnect]);

  // NOTE: there is deliberately NO "pagehide / unmount -> end the
  // meeting" effect here. It used to PATCH status=ended on any page-
  // lifecycle change, which (a) fought the backend's reconnect/reaper
  // design - a refresh, a route teardown, a backend restart or a brief
  // network drop would permanently end an ACTIVE meeting - and (b),
  // because its dependency list held the (then-unstable) `audio` object,
  // its cleanup ran during ordinary rerenders and ended the meeting
  // seconds after it started. Abandonment is now entirely the backend's
  // job: an ACTIVE meeting with no open socket is finalized by the
  // last_activity_at heartbeat + meeting_reaper after its grace period,
  // and a still-present client simply reconnects. Ending is only ever an
  // explicit user action (handleEndMeeting).

  useEffect(() => {
    let cancelled = false;

    async function loadMeeting() {
      try {
        const data = await meetingApi.getMeeting(meetingId);
        if (cancelled) return;

        setMeeting(data);
        setTimeLimit(data.time_limit || null);

        // GET /api/meetings/{id} returns the meeting to its owner in
        // ANY lifecycle state - a 200 here is NOT proof it's live.
        // If it has already ENDED, latch terminal now so nothing
        // below (the connect effect, a queued reconnect) ever opens
        // /ws/audio, which the backend would reject with 403.
        if (data.status === MEETING_STATUS_ENDED) {
          logDebug('meeting_lifecycle', 'ENDED_MEETING_NO_CONNECT');
          enterEndedState();
          return;
        }

        // Page reload/direct navigation into an already-active
        // meeting: started_at is already known, so recover the true
        // elapsed time immediately instead of waiting to reach it
        // through startListening()'s post-connect refetch.
        if (data.started_at && !startedAtMsRef.current) {
          startedAtMsRef.current = new Date(data.started_at).getTime();
        }

        // A reload/reconnect into a meeting that has already been
        // running (started_at set) must not show an empty "Waiting for
        // speech..." while the backend already holds transcript/answer
        // history and the timer reads 00:09:45. Seed the live state from
        // the persisted history once; the WebSocket's own
        // final_transcript/translation/answer events keep updating the
        // SAME entries by transcript_id afterwards, so nothing is
        // duplicated.
        if (data.started_at && !didHydrateRef.current) {
          didHydrateRef.current = true;
          await hydrateLiveStateFromHistory();
        }
      } catch (err) {
        if (!cancelled) {
          reportError('live_meeting_load', err);
          setLoadError(err.message || 'Unable to load meeting.');
          setStatus('disconnected');
        }
      }
    }

    // Best-effort: a failure here just leaves the live view to fill in
    // from the socket as before - it must never block or break the page.
    async function hydrateLiveStateFromHistory() {
      let transcripts = [];
      let answers = [];
      try {
        const [tResult, aResult] = await Promise.all([
          meetingApi.getMeetingTranscripts(meetingId, { page: 1, pageSize: 200 }),
          meetingApi.getMeetingAiResponses(meetingId).catch(() => []),
        ]);
        transcripts = Array.isArray(tResult) ? tResult : tResult?.items || [];
        answers = Array.isArray(aResult) ? aResult : [];
      } catch (_) {
        return;
      }

      if (cancelled || transcripts.length === 0) return;

      const answerByTranscript = new Map(
        answers
          .filter((a) => a.transcript_id != null)
          .map((a) => [String(a.transcript_id), a])
      );

      // Keep only the most recent window, matching the live cap so a
      // reload of a very long meeting behaves exactly like having been
      // connected the whole time.
      const recent = transcripts.slice(-MAX_LIVE_TRANSCRIPT_ENTRIES);

      const byId = {};
      const order = [];
      let lastAnsweredId = null;

      recent.forEach((t) => {
        const id = t.id;
        const ai = answerByTranscript.get(String(id));
        byId[id] = {
          id,
          original: t.original_text,
          translated: t.translated_text || null,
          isQuestion: !!t.is_question,
          timestamp: t.created_at ? new Date(t.created_at) : new Date(),
          answer: ai ? ai.answer : null,
          aiResponseId: ai ? ai.id : null,
          answerRevision: ai ? ai.revision : undefined,
          answerError: null,
          isGeneratingAnswer: false,
          isStreamingAnswer: false,
        };
        order.push(id);
        if (ai) lastAnsweredId = id;
      });

      if (cancelled) return;

      // Merge rather than replace: if the socket connected first and
      // already delivered a live entry, keep whichever fields it set.
      setTranscriptsById((prev) => {
        const next = { ...byId };
        Object.keys(prev).forEach((id) => {
          next[id] = { ...next[id], ...prev[id] };
        });
        return next;
      });
      setTranscriptOrder((prev) => {
        const seen = new Set(prev);
        return [...order.filter((id) => !seen.has(id)), ...prev];
      });
      if (lastAnsweredId) {
        setCurrentQuestionId((current) => current || lastAnsweredId);
      }
    }

    loadMeeting();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meetingId, enterEndedState]);

  useEffect(() => {
    if (!meeting || status !== 'starting') return;

    // An ENDED meeting never gets a live socket. loadMeeting() already
    // latched terminal state and flipped status away from 'starting',
    // so this is belt-and-braces for a direct/reload/back navigation
    // into an ended meeting's /live URL.
    if (meeting.status === MEETING_STATUS_ENDED || meetingEndedRef.current) {
      logDebug('meeting_lifecycle', 'ENDED_MEETING_NO_CONNECT');
      enterEndedState();
      return;
    }

    if (isTabBasedAudioSource(resolveCaptureSource(meeting.audio_source))) {
      // Browser tab/display capture (getDisplayMedia()) must be
      // initiated from an explicit user gesture — it can never be
      // opened automatically from an effect, on load or on reload.
      // Microphone capture has no such restriction and can keep
      // auto-starting.
      setStatus('awaiting_tab_selection');
    } else if (isPhoneOrTablet()) {
      // Except on a phone: iOS refuses to start audio outside a tap
      // (NotAllowedError), so the microphone waits for one too.
      setStatus('awaiting_mic_start');
    } else {
      logDebug('meeting_lifecycle', 'ACTIVE_MEETING_CONNECT');
      startListening();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meeting]);

  useEffect(() => {
    // Ticks continuously (not gated on status === 'listening') so a
    // Pause doesn't freeze the displayed timer — the backend's
    // duration_seconds = ended_at - started_at includes paused time,
    // so the frontend must too, or they'd disagree by however long the
    // meeting was paused.
    const interval = setInterval(() => {
      // Once the meeting is known to be ENDED the elapsed time is fixed
      // (the read-only meeting page shows the final duration_seconds) -
      // the live ticker must not keep counting up forever behind the
      // terminal card.
      if (startedAtMsRef.current && !meetingEndedRef.current) {
        setElapsedSeconds(
          Math.max(0, Math.floor((Date.now() - startedAtMsRef.current) / 1000))
        );
      }
      if (!meetingEndedRef.current) setClockNowMs(Date.now());
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  // Device-change handling: if the active microphone disappears mid-
  // meeting (unplugged, Bluetooth drop), the browser itself ends the
  // audio track, which audioClient.js already turns into a clean
  // onStopped -> status:"disconnected" transition. This listener adds a
  // clearer, specific message instead of the generic disconnect one, so
  // the user understands WHY rather than just that something broke.
  useEffect(() => {
    // Meeting-tab audio has no "microphone device" to monitor — it
    // relies on the captured tab track's own `ended` event instead
    // (see audioClient.js). Only relevant for microphone/mixed.
    // (A meeting-tab session's own microphone is watched, and brought back
    // when the SAME device returns, inside audioClient.js.)
    if (
      status !== 'listening' ||
      !navigator.mediaDevices ||
      resolveCaptureSource(meeting?.audio_source) === AUDIO_SOURCES.MEETING_TAB
    ) {
      return undefined;
    }

    const handleDeviceChange = async () => {
      if (!microphoneId) return;

      try {
        const devices = await navigator.mediaDevices.enumerateDevices();
        const stillExists = devices.some(
          (d) => d.kind === 'audioinput' && d.deviceId === microphoneId
        );

        if (!stillExists) {
          setLoadError(
            'Your microphone was disconnected. Reconnect it or choose a ' +
              'different one from a new meeting.'
          );
        }
      } catch (_) {
        // Best-effort UX only — never let this throw into the app.
      }
    };

    navigator.mediaDevices.addEventListener('devicechange', handleDeviceChange);

    return () =>
      navigator.mediaDevices.removeEventListener(
        'devicechange',
        handleDeviceChange
      );
  }, [status, microphoneId, meeting]);

  // What audioClient.js reports about the microphone it is capturing: its
  // name, when it stops, when it is muted. The microphone returning clears the
  // notice about it going away.
  useEffect(() => {
    const handleMicrophoneChange = (event) => {
      const detail = event.detail || {};

      if (detail.state === 'started') {
        setMicInfo({
          label: detail.label || '',
          deviceId: detail.deviceId || '',
          followsSystem: !!detail.followsSystem,
        });
        setMicMuted(false);
        setMicSilent(false);
        setMicDefaultChanged('');
        setMicNotice('');
      } else if (detail.state === 'stopped') {
        setMicInfo(null);
        setMicMuted(false);
        setMicSilent(false);
      } else if (detail.state === 'muted') {
        setMicMuted(true);
      } else if (detail.state === 'unmuted') {
        setMicMuted(false);
      } else if (detail.state === 'silent') {
        setMicSilent(true);
      } else if (detail.state === 'audible') {
        setMicSilent(false);
      } else if (detail.state === 'default_changed') {
        setMicDefaultChanged(detail.label || 'another microphone');
      }
    };

    microphoneEvents.addEventListener('change', handleMicrophoneChange);

    return () =>
      microphoneEvents.removeEventListener('change', handleMicrophoneChange);
  }, []);

  // The backend rejects any edit (status or otherwise) to a meeting
  // that has already ended with this code (services/meeting_lifecycle.py
  // MeetingUpdateNotAllowed) — used to flip the UI into the terminal
  // "Meeting Ended" state instead of leaving stale actionable buttons.
  const isMeetingEndedError = (err) => err?.code === 'MEETING_NOT_MODIFIABLE';

  const handlePause = () => {
    clearPendingReconnect();
    reconnectAttemptsRef.current = 0;
    stopAudio();
    setStatus('paused');

    meetingApi.updateMeeting(meetingId, { status: 'paused' }).catch((err) => {
      if (isMeetingEndedError(err)) {
        // The backend already ended this meeting (elsewhere, or the
        // reaper). This is a terminal fact, not a retryable pause
        // failure - go straight to the clean terminal UI rather than
        // leaving a red "already ended" banner over live controls.
        enterEndedState();
        return;
      }
      // Category only (services/monitoring.js) - the UI below is unchanged.
      reportError('meeting_control_pause', err);
      setLoadError(err.message || 'Unable to pause meeting.');
    });
  };

  const handleResume = () => {
    meetingApi.updateMeeting(meetingId, { status: 'active' }).catch((err) => {
      if (isMeetingEndedError(err)) {
        enterEndedState();
        return;
      }
      reportError('meeting_control_resume', err);
      setLoadError(err.message || 'Unable to resume meeting.');
    });

    if (isTabBasedAudioSource(resolveCaptureSource(meeting.audio_source))) {
      // Resume itself IS a user click, but getDisplayMedia() needs its
      // OWN direct click — reopening the share picker the instant
      // Resume is pressed would be surprising, and isn't reliably
      // still within the same "transient activation" window after the
      // updateMeeting() call above. Show the explicit reconnect action
      // instead of auto-opening the picker.
      setStatus('awaiting_tab_selection');
      return;
    }

    startListening();
  };

  const handleEndMeeting = async () => {
    logDebug('meeting_lifecycle', 'MEETING_END');
    setIsEndingMeeting(true);
    isEndingRef.current = true;
    // Terminal from this point: any socket close that follows must not
    // be retried.
    meetingEndedRef.current = true;
    clearPendingReconnect();

    try {
      stopAudio();

      // ended_at/duration are computed server-side from the validated
      // status transition — the client no longer sends a timestamp.
      await meetingApi.updateMeeting(meetingId, { status: 'ended' });

      navigate(`/app/meeting/${meetingId}`, { replace: true });
    } catch (err) {
      isEndingRef.current = false;

      if (isMeetingEndedError(err)) {
        // The meeting was ALREADY ended (another tab, or the backend
        // reaper). The user asked to end it and it is ended - that is a
        // success, not an error. Go to the read-only meeting page
        // exactly as the happy path does, instead of stranding the user
        // in the live UI under a red "already ended" banner.
        navigate(`/app/meeting/${meetingId}`, { replace: true });
        return;
      }

      // A real failure to end - this meeting may still be live, so
      // un-latch and surface the reason.
      meetingEndedRef.current = false;
      reportError('meeting_control_end', err);
      setLoadError(err.message || 'Unable to end meeting.');
      setIsEndingMeeting(false);
      setShowEndModal(false);
    }
  };

  if (loadError && !meeting) {
    return <div className="form-alert">{loadError}</div>;
  }

  if (!meeting) {
    return <Loader label="Loading meeting..." />;
  }

  // Terminal: this meeting has ENDED. No live socket was opened and none
  // will be - the transcript, translations and answers are saved and
  // shown on the read-only meeting page.
  if (status === 'ended') {
    return (
      <div className="live-meeting-page">
        <div className="live-meeting-header">
          <div className="live-meeting-title-row">
            <strong>{meeting.title}</strong>
          </div>
        </div>

        <div style={{ padding: '0 var(--space-5)', marginTop: 'var(--space-3)' }}>
          <Card interactive title="This meeting has ended">
            <p>
              Live transcription and KrishvaQ&apos;s AI assistance are no longer running for
              this meeting. Its transcript, translations and answers are
              saved.
            </p>
            <div style={{ display: 'flex', gap: 'var(--space-3)', marginTop: 'var(--space-3)' }}>
              <Button onClick={() => navigate(`/app/meeting/${meetingId}`)}>
                View meeting
              </Button>
              <Button variant="secondary" onClick={() => navigate('/app/history')}>
                Back to Meetings
              </Button>
            </div>
          </Card>
        </div>
      </div>
    );
  }

  const currentQuestionEntry = currentQuestionId
    ? transcriptsById[currentQuestionId]
    : null;

  // The countdown: the server's deadline when it sent one, else 2 hours from
  // started_at (an older backend). Nothing before the meeting has started.
  const countdownSeconds =
    timeLimit || startedAtMsRef.current
      ? secondsRemaining(
          timeLimit,
          startedAtMsRef.current ? new Date(startedAtMsRef.current).toISOString() : null,
          clockNowMs
        )
      : null;
  const countdownHidden = meetingEnded || isTimeLimitCode(usageLimitCode);
  const countdownLevel =
    countdownSeconds === null ? 'none' : warningLevel(countdownSeconds, timeLimit?.kind);
  const countdownWarning = WARNING_MESSAGES[countdownLevel] || '';
  const countdownTone =
    countdownLevel === 'one_minute' || countdownLevel === 'ended'
      ? 'danger'
      : countdownLevel === 'none'
        ? 'ok'
        : 'warning';

  return (
    <div className="live-meeting-page">
      <div className="live-meeting-header">
        <div className="live-meeting-title-row">
          <strong>{meeting.title}</strong>
          {status === 'listening' && <Badge variant="listening" dot>Live</Badge>}
          <span style={{ color: 'var(--color-text-muted)' }}>
            {formatElapsed(elapsedSeconds)}
          </span>
          {countdownSeconds !== null && !countdownHidden && (
            <span
              className={`live-meeting-countdown live-meeting-countdown-${countdownTone}`}
              role="timer"
              aria-live="off"
              title="KrishvaQ ends this meeting when the timer reaches 00:00."
            >
              Meeting time left: {formatCountdown(countdownSeconds)}
            </span>
          )}
        </div>
      </div>

      {countdownWarning && !countdownHidden && (
        <div
          className={`form-alert${countdownLevel === 'one_minute' ? ' form-alert-danger' : ''}`}
          style={{ margin: '0 var(--space-5)', marginTop: 'var(--space-3)' }}
          role={countdownLevel === 'one_minute' ? 'alert' : 'status'}
        >
          {countdownWarning}
        </div>
      )}

      {loadError && (
        <div
          className="form-alert"
          style={{ margin: '0 var(--space-5)', marginTop: 'var(--space-3)' }}
          role="status"
        >
          {loadError}
        </div>
      )}

      {/* Purely additive UI feedback for an in-progress reconnect - the
          StatusBar badge already shows "Reconnecting... Attempt N/MAX"
          for progress; this banner makes the "why" (connection lost)
          clear at a glance without changing any reconnect logic. */}
      {status === 'reconnecting' && !loadError && (
        <div
          className="form-alert"
          style={{ margin: '0 var(--space-5)', marginTop: 'var(--space-3)' }}
          role="status"
        >
          Connection lost. Reconnecting...
        </div>
      )}

      {/* A plan/usage stop, not a transient error: the session is over
          and reconnecting cannot help, so this offers the upgrade path
          and the End Meeting action instead of a retry. */}
      {usageLimitMessage && (
        <div
          className="form-alert"
          style={{ margin: '0 var(--space-5)', marginTop: 'var(--space-3)' }}
          role="status"
        >
          {usageLimitCode === STANDARD_MEETING_CODE ? (
            // Hitting the standard cap is not an upsell moment: nothing is
            // wrong with the plan, the meeting simply ran its full length.
            <>
              <strong>{STANDARD_MEETING_ENDED_TITLE}</strong>{' '}
              {STANDARD_MEETING_ENDED_MESSAGE}{' '}
              <Link to={`/app/meeting/${meetingId}`} className="form-alert-link">
                View meeting
              </Link>
            </>
          ) : (
          <>
          {usageLimitMessage}{' '}
          {usageLimitCode === PASS_DURATION_REACHED ? (
            // A Pay Per Meeting session that used its time: upgrading
            // would not extend it - another meeting is the next step.
            <Link to={PAY_PER_MEETING.buyPath} className="form-alert-link">
              Buy another meeting
            </Link>
          ) : (
            <Link to="/app/billing/plans" className="form-alert-link">
              Upgrade
            </Link>
          )}
          </>
          )}
        </div>
      )}

      {!canCaptureMeetingTab() &&
        resolveCaptureSource(meeting.audio_source) === AUDIO_SOURCES.MICROPHONE && (
        // Said up front, not left to be discovered: this browser cannot
        // capture a meeting tab, so the other participants are only heard
        // if the microphone can hear them - and a phone often will not let
        // a browser record while the meeting app on the same phone is
        // using the microphone.
        <div
          className="form-alert"
          style={{ margin: '0 var(--space-5)', marginTop: 'var(--space-3)' }}
          role="status"
        >
          This browser can&apos;t share a meeting tab (phone and tablet
          browsers can&apos;t), so KrishvaQ is listening through your
          microphone. The other participants are only heard if the meeting
          plays on a speaker the microphone can pick up. If the meeting is
          running on this same phone, the phone usually won&apos;t let the
          browser hear it - use a computer, or run the meeting on another
          device.
        </div>
      )}

      {micNotice && (
        <div
          className="form-alert"
          style={{ margin: '0 var(--space-5)', marginTop: 'var(--space-3)' }}
          role="status"
        >
          {micNotice}
        </div>
      )}

      {micSilent && !micNotice && (
        <div
          className="form-alert"
          style={{ margin: '0 var(--space-5)', marginTop: 'var(--space-3)' }}
          role="status"
        >
          No sound is coming from your microphone
          {micInfo?.label ? ` (${micInfo.label})` : ''}, so your own speech is
          not being transcribed. If it is a Bluetooth headset, another call or
          device may be using it. Check it, or choose a different microphone in
          Settings, then pause and resume the meeting.
        </div>
      )}

      {micDefaultChanged && !micNotice && (
        <div
          className="form-alert"
          style={{ margin: '0 var(--space-5)', marginTop: 'var(--space-3)' }}
          role="status"
        >
          Your computer&apos;s default microphone changed to {micDefaultChanged},
          but KrishvaQ is still using
          {micInfo?.label ? ` ${micInfo.label}` : ' the one it started with'}.
          To switch, pause and resume the meeting.
        </div>
      )}

      {micMuted && !micNotice && (
        <div
          className="form-alert"
          style={{ margin: '0 var(--space-5)', marginTop: 'var(--space-3)' }}
          role="status"
        >
          Your microphone is not sending audio (it may be muted, or switched by
          a Bluetooth headset), so your own speech is not being transcribed
          until it is back.
        </div>
      )}

      <div className="live-meeting-subheader">
        <span className="language-chip-lite">
          {languageLabel(meeting.source_language)} →{' '}
          {languageLabel(meeting.target_language)}
        </span>
        <span>{modeLabel(meeting.mode)}</span>
        <span>{audioSourceHeaderLabel(resolveCaptureSource(meeting.audio_source))}</span>
        {micInfo?.label && (
          <span>
            Microphone: {micInfo.label}
            {micInfo.followsSystem ? ' (system default)' : ''}
          </span>
        )}
      </div>

      {status === 'awaiting_tab_selection' && (
        <div style={{ padding: '0 var(--space-5)', marginTop: 'var(--space-3)' }}>
          <Card interactive title="Connect Meeting Audio">
            <p>To let KrishvaQ hear the client:</p>
            <ol style={{ paddingLeft: 'var(--space-5)' }}>
              <li>
                Click <strong>{hasConnectedTabAudioRef.current ? 'Reconnect Meeting Audio' : 'Select Meeting Tab'}</strong>
              </li>
              <li>Choose &quot;Chrome Tab&quot;</li>
              <li>Select the tab with your meeting (Google Meet, Teams Web, Zoom Web, ...)</li>
              <li>Enable &quot;Share tab audio&quot;</li>
              <li>Click Share</li>
            </ol>
            <Button onClick={startListening}>
              {hasConnectedTabAudioRef.current
                ? 'Reconnect Meeting Audio'
                : 'Select Meeting Tab'}
            </Button>
          </Card>
        </div>
      )}

      {status === 'awaiting_mic_start' && (
        <div style={{ padding: '0 var(--space-5)', marginTop: 'var(--space-3)' }}>
          <Card interactive title="Start Listening">
            <p>
              Play the meeting on a speaker near this phone, then tap Start
              Listening and allow the microphone.
            </p>
            <Button onClick={startListening}>Start Listening</Button>
          </Card>
        </div>
      )}

      <div className="mobile-tabs-only" style={{ padding: '0 var(--space-5)', marginTop: 'var(--space-3)' }}>
        <Tabs
          tabs={[
            { key: 'transcript', label: panelVariant === 'questions' ? 'Questions' : 'Transcript' },
            { key: 'copilot', label: 'KrishvaQ AI' },
          ]}
          activeKey={mobileTab}
          onChange={setMobileTab}
        />
      </div>

      <div className="live-meeting-body" data-mobile-tab={mobileTab}>
        <div className="live-meeting-panel">
          <TranscriptPanel
            variant={panelVariant}
            entries={panelEntries}
            interimText={panelVariant === 'full' ? interimText : ''}
            interimTranslation={panelVariant === 'full' ? interimTranslation : ''}
            onRetryTranslation={handleRetryTranslation}
            selectedQuestionId={currentQuestionId}
            onSelectQuestion={handleSelectQuestion}
          />
        </div>

        <div className="live-meeting-panel">
          <CoPilotPanel
            question={currentQuestionEntry?.original || ''}
            answer={currentQuestionEntry?.answer || ''}
            isGenerating={!!currentQuestionEntry?.isGeneratingAnswer}
            isStreamingAnswer={!!currentQuestionEntry?.isStreamingAnswer}
            error={currentQuestionEntry?.answerError || ''}
            onRetryAnswer={handleRetryAnswer}
            onCorrectAnswer={handleCorrectAnswer}
            onRegenerateAnswer={handleRegenerateAnswer}
            questionTranscriptId={currentQuestionEntry?.id || null}
            showTalkingPoints={user?.preferences?.show_talking_points !== false}
            meetingId={meetingId}
            aiResponseId={currentQuestionEntry?.aiResponseId || null}
            answerRevision={currentQuestionEntry?.answerRevision}
            codeRelated={currentQuestionEntry?.codeRelated ?? null}
            ttsAvailable={meeting.tts_available_for_answer_language !== false}
          />
        </div>
      </div>

      <StatusBar
        status={status}
        meetingEnded={meetingEnded}
        reconnectAttempt={reconnectAttempt}
        maxReconnectAttempts={MAX_RECONNECT_ATTEMPTS}
        onPause={handlePause}
        onResume={handleResume}
        onEndMeeting={() => setShowEndModal(true)}
      />

      {showEndModal && (
        <EndMeetingModal
          onCancel={() => setShowEndModal(false)}
          onConfirm={handleEndMeeting}
          isSubmitting={isEndingMeeting}
        />
      )}
    </div>
  );
}

export default LiveMeetingPage;
